import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import Spinner from './Spinner.jsx';

const VARIANTS = {
  // Primary: rich leaf fill with a whisper of a leaf-700→leaf-800 vertical
  // gradient for depth, carried on a soft *coloured* shadow so it glows in its
  // own hue. Press drops the shadow to shadow-soft for a tactile "push".
  primary:
    'bg-gradient-to-b from-leaf-700 to-leaf-800 text-milk border-leaf-800 shadow-button ' +
    'active:from-leaf-800 active:to-leaf-900 active:shadow-soft sm:hover:from-leaf-800 sm:hover:to-leaf-900',
  accent:
    'bg-gradient-to-b from-persimmon to-persimmon-dark text-white border-persimmon shadow-button ' +
    'active:shadow-soft sm:hover:from-persimmon-dark sm:hover:to-persimmon-dark',
  // Secondary: white pill on a hairline oat border with a soft resting shadow
  // and a gentle oat hover tint.
  secondary:
    'bg-surface text-heading border-oat-dark shadow-soft active:bg-oat active:shadow-none sm:hover:bg-oat sm:hover:border-oat-darker',
  ghost:
    'bg-transparent text-heading border-transparent active:bg-oat/70 sm:hover:bg-oat/70',
  danger:
    'bg-surface text-persimmon-dark border-persimmon/40 shadow-soft active:bg-persimmon-light active:shadow-none sm:hover:bg-persimmon-light sm:hover:border-persimmon/60',
};

const SIZES = {
  // Every size clears 44px in both axes — these are the primary controls on a
  // phone, and a 32px button is a mis-tap waiting to happen.
  sm: 'min-h-touch px-4 py-2 text-sm gap-1.5',
  md: 'min-h-touch px-5 py-3 text-[0.9375rem] gap-2',
  lg: 'min-h-[3.25rem] px-7 py-3.5 text-base gap-2.5',
};

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    type = 'button',
    to,
    href,
    loading = false,
    disabled = false,
    fullWidth = false,
    className = '',
    children,
    ...props
  },
  ref,
) {
  const classes = [
    'inline-flex touch-target items-center justify-center rounded-full border font-semibold tracking-[-0.01em]',
    // ~150ms tactile press: crisp scale-down with a synchronised shadow drop.
    'transition-[background-color,transform,box-shadow,border-color] duration-150 ease-out active:scale-[0.98]',
    // Clear keyboard focus ring that sits off the fill.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf-600 focus-visible:ring-offset-2 focus-visible:ring-offset-milk',
    'disabled:pointer-events-none disabled:opacity-45',
    VARIANTS[variant] ?? VARIANTS.primary,
    SIZES[size] ?? SIZES.md,
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {loading && <Spinner size="sm" label="" />}
      {children}
    </>
  );

  if (to && !disabled && !loading) {
    return (
      <Link ref={ref} to={to} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  if (href && !disabled && !loading) {
    return (
      <a ref={ref} href={href} className={classes} {...props}>
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...props}
    >
      {content}
    </button>
  );
});

export default Button;
